import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Channel } from "../../../../../lib/api/channels";
import { SectionLoader } from "../../../../../components/LogoLoader";
import { useBoardStore } from "../../../../../stores/boardStore";
import { useAuthStore } from "../../../../../stores/authStore";

interface BridgeEntity {
    id: string;
    name: string;
    entityType?: string;
    parentId?: string;
}

interface BridgeViewProps {
    channel: Channel;
    selectedEntityIds: string[];
    isLoading: boolean;
    isValidating: boolean;
    isAnimating: boolean;
    onBack: () => void;
    onClose: () => void;
    onBridgeEntityClick: (entityId: string) => void;
    addedEntityIds?: string[];
    rootEntityId?: string | null;
    highlightedEntityId?: string;
    shouldHighlight?: boolean;
    onClearHighlight?: () => void;
}

export const BridgeView: React.FC<BridgeViewProps> = ({
    channel,
    selectedEntityIds,
    isLoading,
    isValidating,
    isAnimating,
    onBack,
    onClose,
    onBridgeEntityClick,
    addedEntityIds = [],
    rootEntityId = null,
    highlightedEntityId,
    shouldHighlight,
    onClearHighlight,
}) => {
    const { t, i18n } = useTranslation("common");
    const { finance, currencySettings } = useBoardStore();
    const { user } = useAuthStore();

    // Financial calculations
    const entityTotals = useMemo(() => {
        const totals: Record<string, number> = {};
        if (!finance) return totals;

        if (finance.transactions && Array.isArray(finance.transactions)) {
            finance.transactions.forEach((tx: any) => {
                const entityId = String(tx.originalEntityId || tx.entityId || '');
                if (!entityId || entityId === 'unallocated') return;

                const type = (tx.type || tx.transactionType || '').toUpperCase();
                const value = parseFloat(tx.amount || tx.value || 0);
                const convertedValue = tx.convertedAmount || value;

                if (totals[entityId] === undefined) totals[entityId] = 0;
                if (type === 'EXPENSE') totals[entityId] -= Math.abs(convertedValue);
                else if (type === 'REVENUE') totals[entityId] += convertedValue;
            });
            return totals;
        }

        if (finance.breakdown && Array.isArray(finance.breakdown)) {
            finance.breakdown.forEach((item: any) => {
                const entityId = String(item.entityId || item.entity_id || '');
                if (!entityId || entityId === 'unallocated') return;
                const type = (item.type || item.transactionType || '').toUpperCase();
                const value = parseFloat(item.value || item.amount || 0);
                if (totals[entityId] === undefined) totals[entityId] = 0;
                if (type === 'EXPENSE') totals[entityId] -= Math.abs(value);
                else if (type === 'REVENUE') totals[entityId] += value;
            });
        }
        return totals;
    }, [finance]);

    const currencyCode = currencySettings?.default || user?.currency || '';
    const getCurrencySymbol = (code: string) => {
        try {
            return (0).toLocaleString(i18n.language, { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '');
        } catch {
            return '$';
        }
    }
    const symbol = getCurrencySymbol(currencyCode);

    const formatValue = (val: number) => {
        return Math.abs(val).toLocaleString(i18n.language, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    // Helper function to safely extract entities array from channel
    const getEntitiesArray = (chan: any): any[] => {
        if (!chan) return [];
        let entities = chan.entities || [];
        // Handle case where entities is wrapped in an object with entities property
        if (!Array.isArray(entities) && typeof entities === 'object' && Array.isArray(entities.entities)) {
            entities = entities.entities;
        }
        return Array.isArray(entities) ? entities : [];
    };

    const getAggregatedValue = React.useCallback((entityId: string): number | null => {
        let total = entityTotals[entityId] || 0;
        let hasTransactions = entityTotals[entityId] !== undefined;
        const entities = getEntitiesArray(channel);

        if (entities.length > 0) {
            const children = entities.filter((e: any) => String(e.parentId) === String(entityId));
            children.forEach(child => {
                const childValue = getAggregatedValue(child.id);
                if (childValue !== null) {
                    total += childValue;
                    hasTransactions = true;
                }
            });
        }
        return hasTransactions ? total : null;
    }, [entityTotals, channel]);

    const renderChannelIcon = () => {
        if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
            return <img src={channel.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
            return <img src={channel.channelTypeConfig.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.icon) {
            return <span className="text-xl leading-none">{channel.icon}</span>;
        }
        return (
            <span className="w-6 h-6 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        );
    };

    const getRootEntities = (): BridgeEntity[] => {
        const entities = getEntitiesArray(channel);
        return entities.filter((e: any) => !e.parentId);
    };

    const getChildrenInfo = (entityId: string): { count: number; label: string } => {
        const entities = getEntitiesArray(channel);
        const children = entities.filter((e: any) => String(e.parentId) === String(entityId));
        if (children.length === 0) return { count: 0, label: '' };
        const typeCounts: Record<string, number> = {};
        children.forEach((child: any) => {
            const type = child.entityType || 'item';
            typeCounts[type] = (typeCounts[type] || 0) + 1;
        });
        const mostCommonType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'item';
        // Use i18n plural keys: entityTypes.campaign_one / entityTypes.campaign_other
        const normalizedKey = mostCommonType.replace(/_/g, '');
        const label = t(`entityTypes.${normalizedKey}`, { count: children.length })
            || mostCommonType.replace(/_/g, ' ');
        return { count: children.length, label };
    };

    const getSelectedChildrenCount = (entityId: string): number => {
        const entities = getEntitiesArray(channel);
        const parentIdStr = String(entityId);
        const selectedChildren = entities.filter((entity: any) => {
            const entityParentId = entity.parentId != null ? String(entity.parentId) : null;
            const entityIdStr = entity.id != null ? String(entity.id) : null;
            return entityParentId === parentIdStr && entityIdStr && selectedEntityIds.includes(entityIdStr);
        });
        return selectedChildren.length;
    };

    const [searchQuery, setSearchQuery] = React.useState("");
    const itemRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

    const filteredEntities = useMemo(() => {
        const rootEntities = getRootEntities();
        const entitiesToDisplay = rootEntityId
            ? getEntitiesArray(channel).filter((e: any) => String(e.parentId) === String(rootEntityId))
            : rootEntities;

        return entitiesToDisplay
            .filter((entity: any) => entity.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .sort((a: any, b: any) => {
                const valA = Math.abs(getAggregatedValue(a.id) || 0);
                const valB = Math.abs(getAggregatedValue(b.id) || 0);
                return valB - valA;
            });
    }, [searchQuery, channel, getAggregatedValue, rootEntityId]);

    const hasScrolledRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (shouldHighlight && highlightedEntityId && itemRefs.current[highlightedEntityId]) {
            // Check if we already scrolled for this specific ID in this session
            if (hasScrolledRef.current === highlightedEntityId) return;

            const element = itemRefs.current[highlightedEntityId];
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Mark as scrolled
            hasScrolledRef.current = highlightedEntityId;

            // Add highlight pulse
            element.classList.add('entity-highlight-pulse');

            // Clear highlight flag in parent state
            if (onClearHighlight) onClearHighlight();

            const timer = setTimeout(() => {
                if (element) element.classList.remove('entity-highlight-pulse');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [shouldHighlight, highlightedEntityId, filteredEntities, onClearHighlight]);

    // Reset scroll lock when ID changes or shouldHighlight is reset
    React.useEffect(() => {
        if (!shouldHighlight) {
            hasScrolledRef.current = null;
        }
    }, [shouldHighlight, highlightedEntityId]);

    const channelIsLoading = (channel as any).isLoadingEntities || isLoading;

    return (
        <div className="flex flex-col" style={{ height: '75vh' }}>
            <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 z-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button
                            onClick={onBack}
                            className="flex-shrink-0 p-1.5 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-1"
                            disabled={isValidating || isAnimating}
                        >
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1205ef32.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1205ef32.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 text-xl overflow-hidden shadow-sm">
                            {renderChannelIcon()}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h2 className="text-base font-bold text-neutral-600 dark:text-neutral-100 leading-none mb-0.5 truncate">
                                {channel.name}
                            </h2>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">
                                {t("itemSelection.selectAccount")}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 ml-3 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors focus:outline-none"
                    >
                        <span className="w-5 h-5 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                </div>
            </div>

            <div className="flex-shrink-0 px-6 pb-2 pt-2 bg-white dark:bg-neutral-950 border-b border-neutral-100 dark:border-neutral-800">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="h-4 w-4 text-neutral-400 dark:text-neutral-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9bf91cd6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9bf91cd6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-9 pr-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm bg-neutral-50 dark:bg-neutral-900 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:bg-white dark:focus:bg-neutral-800 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary transition-colors dark:text-neutral-100"
                        placeholder={t("common.search") || "Search..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 p-6">
                {channelIsLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 h-full">
                        <SectionLoader size="sm" message={String(t("channels.loadingEntities"))} />
                    </div>
                ) : filteredEntities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-neutral-500 h-full">
                        <p>{rootEntityId ? t("common.noResults") : t("channels.noEntitiesFound")}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <AnimatePresence mode="popLayout">
                            {filteredEntities.map((entity: any) => {
                                const childrenInfo = getChildrenInfo(entity.id);
                                const selectedCount = getSelectedChildrenCount(entity.id);
                                const isAdded = addedEntityIds.includes(entity.id);
                                const isSelected = isAdded || selectedCount > 0;

                                return (
                                    <motion.button
                                        ref={el => { itemRefs.current[entity.id] = el; }}
                                        layout
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
                                        transition={{ duration: 0.2 }}
                                        key={entity.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onClearHighlight) onClearHighlight();
                                            if (isSelected) return;
                                            onBridgeEntityClick(entity.id);
                                        }}
                                        disabled={isSelected}
                                        className={`w-full group border-2 border-neutral-200 dark:border-neutral-800 rounded-xl p-4 transition-colors duration-200 text-left ${entity.id === highlightedEntityId ? 'border-accent-primary ring-2 ring-accent-primary/20' : isSelected ? 'bg-neutral-50 dark:bg-neutral-900/40 opacity-60 cursor-not-allowed' : 'bg-white dark:bg-neutral-900 hover:border-accent-primary/50 hover:bg-accent-primary/5'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/10 transition-colors">
                                                {renderChannelIcon()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                                                    {entity.name}
                                                </h3>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                                                    <span className="truncate">
                                                        {childrenInfo.count > 0
                                                            ? `${childrenInfo.count} ${childrenInfo.label}`
                                                            : t(`entityTypes.${(entity.entityType || '').replace(/_/g, '')}`) || entity.entityType?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || t('channels.noElements')
                                                        }
                                                    </span>
                                                    {isSelected && (
                                                        <span className="flex-shrink-0 bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-700">
                                                            {t("common.alreadySelected")}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>

                                            {(() => {
                                                const val = getAggregatedValue(entity.id);
                                                if (val === null) return null;
                                                const isNegative = val < 0;
                                                return (
                                                    <div className={`text-xs font-bold whitespace-nowrap flex-shrink-0 ${isNegative ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                        {symbol}{formatValue(val)}
                                                    </div>
                                                );
                                            })()}

                                            {isSelected ? (
                                                <div className="flex-shrink-0 text-emerald-500">
                                                    <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                </div>
                                            ) : (
                                                <span className="text-sm font-medium text-accent-primary">
                                                    {t('boards.addToBoard')}
                                                </span>
                                            )}
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
};
